import * as React from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button, Field, Textarea } from "@/components/ui";
import { EnvContext } from "@/contexts/appContexts";
import { saveElementToFirebase } from "@/utils/firebase";
import type { NewDesignFormValues } from "../NewDesignPage";

interface NotesFieldProps {
  form: UseFormReturn<NewDesignFormValues>;
  disabled?: boolean;
}

export const NOTES_MAX = 600;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const NotesField: React.FC<NotesFieldProps> = ({ form, disabled }) => {
  const envValues = React.useContext(EnvContext);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [imageError, setImageError] = React.useState<string | undefined>();
  const notes = useWatch({ control: form.control, name: "notes" }) ?? "";
  const referenceImageUrl = useWatch({ control: form.control, name: "referenceImageUrl" });
  const { errors } = form.formState;

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError("Images must be under 10 MB.");
      return;
    }
    setImageError(undefined);
    setUploading(true);
    try {
      const url = await saveElementToFirebase(envValues, "design-references", file, uuidv4());
      form.setValue("referenceImageUrl", url, { shouldDirty: true, shouldValidate: true });
    } catch {
      toast.error("We couldn't upload that image. Try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Field label={<span className="sr-only">Notes (optional)</span>} error={errors.notes?.message}>
        {controlProps => (
          <div className="flex flex-col gap-1">
            <Textarea
              {...controlProps}
              {...form.register("notes")}
              rows={3}
              disabled={disabled}
              placeholder="Anything else? e.g. warm and friendly, no exclamation points"
            />
            <span className="self-end text-xs text-slate-400 tabular-nums">
              {notes.length}/{NOTES_MAX}
            </span>
          </div>
        )}
      </Field>

      <Field
        label="Reference image (optional)"
        description="We'll match its layout and style."
        error={imageError ?? errors.referenceImageUrl?.message}
      >
        <div className="flex items-center gap-3">
          {referenceImageUrl && (
            <img
              src={referenceImageUrl}
              alt="Reference"
              className="size-16 rounded-lg border border-slate-200 object-cover"
            />
          )}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="reference-input"
            onChange={onFile}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={uploading}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            <Upload aria-hidden="true" />
            Upload
          </Button>
          {referenceImageUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || uploading}
              onClick={() => form.setValue("referenceImageUrl", undefined, { shouldDirty: true })}
            >
              <X aria-hidden="true" />
              Remove
            </Button>
          )}
        </div>
      </Field>
    </div>
  );
};
